import type { CaseChecklistSection, CaseChecklistTone } from './caseChecklist';

const TONE_CLASSES: Record<CaseChecklistTone, { card: string; badge: string; bullet: string }> = {
    rose: {
        card: 'bg-rose-50 border-rose-100',
        badge: 'bg-rose-100 text-rose-800',
        bullet: 'bg-rose-300'
    },
    amber: {
        card: 'bg-amber-50 border-amber-100',
        badge: 'bg-amber-100 text-amber-800',
        bullet: 'bg-amber-300'
    }
};

interface ChecklistSectionCardProps {
    section: CaseChecklistSection;
    className?: string;
}

function ChecklistSectionCard({ section, className = '' }: ChecklistSectionCardProps) {
    const tone = TONE_CLASSES[section.tone];

    return (
        <section
            className={`flex flex-col rounded-xl border overflow-hidden ${tone.card} ${className}`}
        >
            <header className="flex items-center gap-3 px-4 py-3">
                <span
                    className={`w-6 h-6 flex-shrink-0 rounded-full flex items-center justify-center text-xs font-bold ${tone.badge}`}
                >
                    {section.number}
                </span>
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-gray-800 uppercase tracking-wide">
                        {section.title}
                    </div>
                    <div className="text-xs text-gray-500">{section.views}</div>
                </div>
            </header>
            <ul className="flex-1 bg-white/70 border-t border-white divide-y divide-gray-100">
                {section.items.map((item) => (
                    <li key={item.id} className="flex items-start gap-3 px-4 py-2.5">
                        <span
                            aria-hidden="true"
                            className={`mt-[7px] w-1.5 h-1.5 flex-shrink-0 rounded-full ${tone.bullet}`}
                        />
                        <ChecklistItemLabel label={item.label} />
                    </li>
                ))}
            </ul>
        </section>
    );
}

// Bold the lead term ("Overjet:") so the list scans by topic.
function ChecklistItemLabel({ label }: { label: string }) {
    const colon = label.indexOf(':');
    if (colon === -1) return <span className="text-sm text-gray-700">{label}</span>;
    return (
        <span className="text-sm text-gray-700">
            <span className="font-semibold text-gray-800">{label.slice(0, colon + 1)}</span>
            {label.slice(colon + 1)}
        </span>
    );
}

export { ChecklistSectionCard };
